import animate from '@play-co/timestep-core/lib/animate';
import View from '@play-co/timestep-core/lib/ui/View';
import { createEmitter } from 'src/lib/Emitter';
import { animDuration, getScreenDimensions } from 'src/lib/utils';
import ImageScaleView, {
  ImageScaleViewOpts,
} from '@play-co/timestep-core/lib/ui/ImageScaleView';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import sounds from 'src/lib/sounds';
import i18n from 'src/lib/i18n/i18n';
import StateObserver from 'src/StateObserver';
import { hideLoading, showLoading } from 'src/state/ui';
import { AssetGroup } from '@play-co/timestep-core';

export type Match2BasePopupFullscreenCTAOptions = {
  localeText?: () => string;
  onClick?: () => void | Promise<void>;
  width?: number;
  footerHeight?: number;
  noBackground?: boolean;
  ctaOffsetY?: number;
};

export interface PopupMatch2BaseFullscreenOptions {
  superview: View;
  bgImageOpts?: Omit<
    ImageScaleViewOpts,
    'centerAnchor' | 'centerOnOrigin' | 'canHandleEvents' | 'infinite'
  >;
  bgColor?: string;
  zIndex?: number;
  cta?: Match2BasePopupFullscreenCTAOptions;
  tracking: { feature: string; $subFeature: string };
}

export default abstract class PopupMatch2BaseFullscreen<
  CloseHandler extends Function = () => void,
> {
  protected root: View;
  protected panel: View | ImageScaleView;
  protected footer?: ImageScaleView;
  protected buttonClose?: ButtonScaleView;
  protected buttonCta?: ButtonScaleViewWithText;
  protected overlay: View;
  private screenHeight = 0;
  private screenTop = 0;

  constructor(
    protected opts: PopupMatch2BaseFullscreenOptions & { close?: CloseHandler },
  ) {
    const screen = getScreenDimensions();
    const width = opts.superview.style.width;
    const height = screen.height;

    this.root = new View({
      width,
      height,
      zIndex: opts.zIndex || 9998,
      infinite: true,
    });

    const panelImageOpts = opts.bgColor
      ? { backgroundColor: opts.bgColor }
      : opts.bgImageOpts;

    this.panel = new ImageScaleView({
      superview: this.root,
      width: width + 1,
      height,
      x: -1,
      ...panelImageOpts,
    });

    this.overlay = new View({
      superview: this.root,
      infinite: true,
      zIndex: 10000,
      visible: false,
    });

    if (opts.close) {
      this.buttonClose = new ButtonScaleView({
        ...buttons.close,
        superview: this.panel,
        x: this.panel.style.width + -buttons.close.width * 0.5 - 16,
        y: buttons.close.height * 0.5 + 16,
        onClick: async () => {
          sounds.playSound('cancel');
          opts.close!();
        },
        tracking: {
          feature: this.opts.tracking.feature,
          $subFeature: `${this.opts.tracking.$subFeature}-match2-close`,
        },
      });
    }

    if (opts.cta) {
      const ctaHeight = 90;
      const ctaWidth = opts.cta.width ?? 235;
      const ctaOffsetY = opts.cta.ctaOffsetY ?? 0;
      const footerHeight = opts.cta.footerHeight ?? 170;

      if (!opts.cta.noBackground) {
        this.footer = new ImageScaleView({
          superview: this.root,
          image: 'assets/ui/match2/common/panel_footer.png',
          scaleMethod: 'stretch',
          width: width + 2,
          height: footerHeight,
          x: -1,
          canHandleEvents: false,
        });
      }

      const onClick = async () => {
        sounds.sfx.play('ok');
        await opts.cta!.onClick?.();
      };

      this.buttonCta = new ButtonScaleViewWithText({
        ...buttons.secondary,
        fontSize: 40,
        labelOffsetY: -2,
        font: 'Body',
        width: ctaWidth,
        height: ctaHeight,
        x: width * 0.5,
        y: this.footer ? footerHeight * 0.62 : height - ctaHeight + ctaOffsetY,
        superview: this.footer ?? this.root,
        localeText: opts.cta.localeText ?? (() => i18n('basic.okay')),
        onClick,
        centerOnOrigin: true,
        tracking: {
          feature: this.opts.tracking.feature,
          $subFeature: `${this.opts.tracking.$subFeature}-match2-cta`,
        },
      });
    }

    // anchor elements
    createEmitter(this.root, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        this.panel.updateOpts({
          height: screen.height,
          y: screen.top,
          opacity: 1,
        });
        this.screenTop = screen.top;
        this.screenHeight = screen.height;
        if (this.footer) {
          this.footer.style.y =
            screen.height + screen.top - this.footer.style.height;
        } else if (this.buttonCta) {
          const offsetY = this.opts.cta?.ctaOffsetY ?? 0;
          this.buttonCta.style.y =
            screen.height + screen.top - this.buttonCta.style.height + offsetY;
        }
      },
    );
  }

  public getView(): View {
    return this.root;
  }

  public init(opts: any) {
    this.fadeIn();
  }

  public async fadeIn(interactivity = true) {
    this.setInteractivity(false);
    this.attachRoot();
    this.panel.updateOpts({ opacity: 0 });
    this.footer?.updateOpts({ y: this.screenHeight + this.screenTop });

    // Just after showing the bg, load required assets
    await this.loadAssets();

    await this.postLoadingInit();

    return new Promise<void>((resolve) => {
      animate(this.panel)
        .clear()
        .then({ opacity: 1 }, animDuration, animate.easeOut)
        .then(() => {
          this.setInteractivity(interactivity);
          resolve();
        });
      if (this.footer) {
        animate(this.footer)
          .clear()
          .then(
            {
              y: this.screenHeight + this.screenTop - this.footer.style.height,
            },
            animDuration,
            animate.easeOut,
          );
      }
    });
  }

  public fadeOut() {
    return new Promise<void>((resolve) => {
      this.setInteractivity(false);
      animate(this.panel)
        .clear()
        .then({ opacity: 0 }, animDuration, animate.easeOut)
        .then(() => {
          this.detachRoot();
          this.setInteractivity(true);
          resolve();
        });
      if (this.footer) {
        animate(this.footer)
          .clear()
          .then(
            { y: this.screenHeight + this.screenTop },
            animDuration,
            animate.easeOut,
          );
      }
    });
  }

  public setInteractivity(v: boolean) {
    v ? this.overlay.hide() : this.overlay.show();
  }

  protected attachRoot() {
    this.opts.superview.addSubview(this.root);
  }

  protected detachRoot() {
    this.root.removeFromSuperview();
  }

  public get footerHeight() {
    return (this.footer?.style.height ?? 0) * 0.82;
  }

  protected getAssetsGroup(): AssetGroup | null {
    // Can be overridden by subclasses
    return null;
  }

  protected async postLoadingInit(): Promise<void> {
    // Can be overridden by subclasses
  }

  protected async loadAssets() {
    const assetsGroup = this.getAssetsGroup();
    if (!assetsGroup || assetsGroup.isLoaded()) return;

    StateObserver.dispatch(showLoading());
    await assetsGroup.load();
    StateObserver.dispatch(hideLoading());
  }
}
